import {
  type AtlPageDetailPublic,
  type AtlPagePatchResult,
  AtlService,
  type HttpValidationError,
  type UpdateAtlPage,
} from "@common/client"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { aircraftBaseKey } from "./aircraft"
import { atlPageWithUpdate, isPredictableUpdate } from "./optimistic-atl-page"
import { aircraftUtilizationKey, utilizationBaseKey } from "./utilization"
import {
  makeAxiosMutation,
  makeGroupPages,
  makeInfiniteQuery,
  makeQuery,
  unwrap,
} from "./utils"

export const atlPagesBaseKey = ["atl-pages"] as const

export const atlPageKey = (atlPageId: string) =>
  [...atlPagesBaseKey, "detail", atlPageId] as const

const atlPagesListKey = [...atlPagesBaseKey, "list"] as const

const broughtForwardKey = [...atlPagesBaseKey, "brought-forward"] as const

export type AtlPagesParams = Omit<
  Parameters<typeof AtlService.listAtlPages>[0],
  "skip" | "limit"
>

export const ATL_PAGES_PAGE_SIZE = 50

const VERIFYING_POLL_MS = 3000

export const atlPagesInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: AtlPagesParams, page) =>
    AtlService.listAtlPages({ ...params, ...page }),
  queryKey: (params) => [...atlPagesListKey, params],
  pageSize: ATL_PAGES_PAGE_SIZE,
})

export const atlPageGroupPages = makeGroupPages({
  serviceCall: (params: AtlPagesParams, page) =>
    AtlService.listAtlPages({ ...params, ...page }),
  queryKey: (params) => [...atlPagesListKey, "grouped", params],
  pageSize: ATL_PAGES_PAGE_SIZE,
})

export const atlPageQuery = makeQuery({
  serviceCall: AtlService.getAtlPage,
  queryKey: ({ atl_page_id }) => atlPageKey(atl_page_id),
  refetchInterval: (query) =>
    query.state.data?.verifying ? VERIFYING_POLL_MS : false,
})

export const atlBroughtForwardQuery = makeQuery({
  serviceCall: AtlService.atlPageBroughtForward,
  queryKey: (params) => [...broughtForwardKey, params],
})

export const useEnterAtlPageMutation = makeAxiosMutation({
  serviceCall: AtlService.enterAtlPage,
  invalidates: ({ atlPageEntryCreate }) => [
    atlPagesBaseKey,
    aircraftBaseKey,
    aircraftUtilizationKey(atlPageEntryCreate.aircraft_id),
  ],
})

const updateAtlPageMutationKey = [...atlPagesBaseKey, "update"] as const

const updateAtlPageScope = { id: "update-atl-page" }

export const useOptimisticAtlPageUpdateMutation = (
  atlPageId: string,
  {
    onError,
  }: {
    onError: (error: AxiosError<HttpValidationError>) => void
  },
) => {
  const queryClient = useQueryClient()
  const mutationKey = [...updateAtlPageMutationKey, atlPageId]
  const othersSaving = () => queryClient.isMutating({ mutationKey }) > 1

  return useMutation<
    AtlPagePatchResult,
    AxiosError<HttpValidationError>,
    { atl_page_id: string; updateAtlPage: UpdateAtlPage }
  >({
    mutationKey,
    scope: updateAtlPageScope,
    mutationFn: (input) => AtlService.applyAtlPageUpdate(input).then(unwrap),
    onMutate: async (input) => {
      if (!isPredictableUpdate(input.updateAtlPage)) return
      const cached = { queryKey: atlPageKey(input.atl_page_id), exact: true }
      if (!othersSaving()) {
        await queryClient.cancelQueries(cached)
      } else if (queryClient.isFetching(cached) > 0) {
        await queryClient.refetchQueries(cached, { cancelRefetch: false })
      }
      queryClient.setQueryData<AtlPageDetailPublic>(
        cached.queryKey,
        (page) => page && atlPageWithUpdate(page, input.updateAtlPage),
      )
    },
    onError,
    onSettled: (_result, _error, input) => {
      const predicted = isPredictableUpdate(input.updateAtlPage)
      const refetchPage = !predicted || !othersSaving()
      const settled = Promise.all(
        [
          ...(refetchPage ? [atlPageKey(input.atl_page_id)] : []),
          atlPagesListKey,
          broughtForwardKey,
          aircraftBaseKey,
          utilizationBaseKey,
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      )
      return predicted ? undefined : settled
    },
  })
}

export const useRecordAtlPageMutation = makeAxiosMutation({
  serviceCall: AtlService.recordAtlPage,
  scope: updateAtlPageScope,
  invalidates: () => [atlPagesBaseKey, aircraftBaseKey, utilizationBaseKey],
})

export const useVoidAtlPageMutation = makeAxiosMutation({
  serviceCall: AtlService.voidAtlPage,
  invalidates: () => [
    atlPagesListKey,
    broughtForwardKey,
    aircraftBaseKey,
    utilizationBaseKey,
  ],
})
